import axios from 'axios';
import Item from "@/models/Items"
import baseURL from '@/API';
import axiosInstance from "@/utils/axiosConfig";


const API_URL = baseURL + '/items';

// Endpoints statiques pour les différentes actions
const ENDPOINTS = {
    ITEMS: '/items/all_items/',  // Endpoint DataTable pour fetchItems
    ITEMS_EXPORT: '/items/all_item/',
    ITEMS_ARCHIVE: '/items/archive/',
    ITEMS_ARCHIVE_BATCH: '/items/archive/batch/',
    ITEMS_ARCHIVE_IMPORT_EXCEL: '/items/archive/import-excel/'
};

interface ErrorResponse {
    status: number;
    message: string;
}



const ItemService = {

    async getAll(url: string) { 
        try {
            // Construire l'URL complète avec l'endpoint DataTable spécifique
            let fullUrl;
            if (url.startsWith('http')) {
                fullUrl = url;
            } else {
                // Retirer le ? du début de url s'il existe
                const cleanUrl = url.startsWith('?') ? url.substring(1) : url;
                // Utiliser l'endpoint DataTable spécifique pour les items
                fullUrl = `${baseURL}${ENDPOINTS.ITEMS}?${cleanUrl}`;
            }
            
            // Détecter si c'est un export (Excel/CSV)
            const isExport = url.includes('export=excel') || url.includes('export=csv');
            
            // Si c'est un export, on attend un blob (fichier binaire)
            const response = await axiosInstance.get(fullUrl, {
                ...(isExport && { responseType: 'blob' })
            });
            
            // Si c'est un export, retourner directement la réponse blob
            if (isExport) {
                return response;
            }
            
            // Sinon, retourner les données formatées pour le DataTable
            return {
                data: response.data.data || response.data.results,
                totalRows: response.data.recordsFiltered || response.data.count,
                recordsFiltered: response.data.recordsFiltered || response.data.count,
                recordsTotal: response.data.recordsTotal || response.data.count,
                draw: response.data.draw
            };
        } catch (error: any) {
            return error;
        }
    },
    async extractAll(queryParams: string) {
        try {
            // Utiliser l'endpoint d'export spécifique
            const response = await axiosInstance.get(`${baseURL}${ENDPOINTS.ITEMS_EXPORT}?${queryParams}`, {
                responseType: 'blob' // Indique qu'on attend un fichier binaire
            });
            return response;
        } catch (error: any) {
            console.error('Erreur dans ItemService.extractAll:', error);
            return error;
        }
    },

    async getItemArchive(url: string) {
        try {
            // Construire l'URL complète avec l'endpoint d'archive
            const fullUrl = url.startsWith('http') ? url : `${baseURL}${ENDPOINTS.ITEMS_ARCHIVE}?${url}`;
            
            // Détecter si c'est un export (Excel/CSV) ou une requête de données normale
            const isExport = url.includes('export=excel') || url.includes('export=csv');
            
            // Si c'est un export, on attend un blob (fichier binaire)
            const response = await axiosInstance.get(fullUrl, {
                ...(isExport && { responseType: 'blob' })
            });
            
            // Si c'est un export, retourner directement la réponse blob
            if (isExport) {
                return response;
            }
            
            // Sinon, retourner les données formatées pour le DataTable
            return {
                data: response.data.data || response.data.results,
                totalRows: response.data.recordsFiltered || response.data.count,
                recordsFiltered: response.data.recordsFiltered || response.data.count,
                recordsTotal: response.data.recordsTotal || response.data.count,
                draw: response.data.draw
            };
        } catch (error) {
            if (axios.isAxiosError(error)) {
                return {
                    status: error.response?.status ?? 500,
                    message: error.response?.data?.message ?? 'An error occurred'
                };
            }
            return {
                status: 500,
                message: 'An unexpected error occurred'
            };
        }
    },
    async archiveItem({ id, commentaire, action }: { id: number, commentaire: string, action: string }) {
        try {
            const response = await axiosInstance.post(`${API_URL}/archive-item/`, {
                id,
                commentaire,
                action,
            });

            // Vérifiez le statut de la réponse et retournez les données
            if (response.status === 200) {
                return response.data; // Retourne { message: "Item archived successfully" }
            } else {
                throw new Error(response.data?.message || 'Une erreur est survenue.');
            }
        } catch (error: any) {
            console.error('Erreur dans le service:', error.response?.data || error.message);
            throw new Error(error.response?.data?.message || error.message || 'Une erreur est survenue.');
        }
    },

    async archiveItemsBatch(itemsId: number[]) {
        try {
            const response = await axiosInstance.post(`${baseURL}${ENDPOINTS.ITEMS_ARCHIVE_BATCH}`, {
                items_id: itemsId,
            });

            if (response.status === 200) {
                return response.data;
            }

            throw new Error(response.data?.message || 'Une erreur est survenue lors de l\'archivage en masse.');
        } catch (error: any) {
            console.error('Erreur dans ItemService.archiveItemsBatch:', error.response?.data || error.message);
            throw new Error(error.response?.data?.message || error.message || 'Une erreur est survenue lors de l\'archivage en masse.');
        }
    },

    async importArchiveItemsExcel(formData: FormData) {
        try {
            const response = await axiosInstance.post(
                `${baseURL}${ENDPOINTS.ITEMS_ARCHIVE_IMPORT_EXCEL}`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            if (response.status === 200) {
                return response.data;
            }

            // On laisse la gestion détaillée des erreurs au module Vuex
            throw {
                response: {
                    status: response.status,
                    data: response.data,
                },
            };
        } catch (error: any) {
            console.error('Erreur dans ItemService.importArchiveItemsExcel:', error?.response?.data || error?.message);
            throw error;
        }
    },

    async AnnulerAffectation({ id }: { id: number }) {
        try {
            const response = await axiosInstance.post(`${API_URL}/annuler-affectation/${id}/`)

            // Vérifiez le statut de la réponse et retournez les données
            if (response.status === 200) {
                return response.data; // Retourne { message: "Item archived successfully" }
            } else {
                throw new Error(response.data?.message || 'Une erreur est survenue.');
            }
        } catch (error: any) {
            console.error('Erreur dans le service:', error.response?.data || error.message);
            throw new Error(error.response?.data?.message || error.message || 'Une erreur est survenue.');
        }
    },


    async getByIdItem(id: number): Promise<Item | ErrorResponse> {
        try {
            const response = await axiosInstance.get(`${API_URL}/get_item/${id}/`);
            return response.data as Item;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                return {
                    status: error.response?.status ?? 500,
                    message: error.response?.data?.message ?? 'An error occurred while retrieving the ltem.'
                };
            }
            return {
                status: 500,
                message: 'An unexpected error occurred.'
            };
        }
    },

    async updateItem({ Id, Items }: { Id: number, Items: any }): Promise<number | ErrorResponse> {
        try {
            const response = await axiosInstance.put(`${API_URL}/update_item/${Id}/`, Items);
            return response.status;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                return {
                    status: error.response?.status ?? 500,
                    message: error.response?.data?.message ?? 'An error occurred while updating the item.'
                };
            }
            return {
                status: 500,
                message: 'An unexpected error occurred.'
            };
        }
    },


};


export default ItemService;
